import { offset } from '@floating-ui/dom'
import WidgetContext from 'embed_v2/components/Context'
import useFloating from 'embed_v2/hooks/useFloating'
import { Fragment } from 'preact'
import { useState, useEffect, useContext } from 'preact/hooks'
import PropTypes from 'prop-types'
import { getStylesFn } from 'core/helpers/styles'
import { ElementReminderOptions } from 'core/types/element/element_reminder'
import optionsPropTypes from 'PropsSpec'
import Calendar from './reminder/Calendar'
import Phone from './reminder/Phone'

const extractStyles = getStylesFn(
  [
    'textAlign',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'border',
    'color',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'backgroundColor'
  ],
  {
    width: '100%'
  }
)

const extractActionStyles = getStylesFn([], {
  textDecoration: 'underline',
  userSelect: 'none',
  cursor: 'pointer'
})

type Props = {
  options: ElementReminderOptions
}

const Reminder = (props: Props) => {
  const { thankYouData, updateViewer } = useContext(WidgetContext)
  const [showPhone, setShowPhone] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const floatingCalendar = useFloating<HTMLSpanElement>({ middleware: [offset(10)] })
  const floatingPhone = useFloating<HTMLSpanElement>({ middleware: [offset(10)] })

  useEffect(() => {
    const handleClickOutside = () => {
      setShowPhone(false)
      setShowCalendar(false)
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const clickCalendar = (e: MouseEvent) => {
    e.stopPropagation()
    setShowCalendar(prev => !prev)
    setShowPhone(false)
  }

  const clickSMS = (e: MouseEvent) => {
    e.stopPropagation()
    setShowPhone(prev => !prev)
    setShowCalendar(false)
  }

  const submitSmsForm = () => updateViewer()

  const closeSmsForm = () => {
    setShowPhone(false)
  }

  const styles = extractStyles(props.options)
  const actionStyles = {
    ...extractActionStyles(props.options),
    color: `rgba(${props.options.link_color})`
  }

  return (
    <div className="wf_reset wf_reminder" style={styles}>
      <span className="wf_reset wf_reminder_wrapper" style={{ position: 'relative' }}>
        {props.options.calendar_enabled && (
          <Fragment>
            <span
              ref={floatingCalendar.refs.setReference}
              className="wf_reset wf_reminder_calendar"
              style={actionStyles}
              onClick={clickCalendar}
            >
              {props.options.calendar_text}
            </span>
            {showCalendar && thankYouData?.calendar_urls && (
              <Calendar
                ref={floatingCalendar.refs.setFloating}
                styles={{
                  position: floatingCalendar.strategy,
                  top: floatingCalendar.y ?? 0,
                  left: floatingCalendar.x ?? 0
                }}
                urls={thankYouData?.calendar_urls}
              />
            )}
          </Fragment>
        )}
        {props.options.calendar_enabled && props.options.sms_enabled && (
          <span className="wf_reset" style={{ margin: '0 4px' }}>
            or
          </span>
        )}
        {props.options.sms_enabled && (
          <Fragment>
            <span
              ref={floatingPhone.refs.setReference}
              className="wf_reset wf_reminder_phone"
              style={actionStyles}
              onClick={clickSMS}
            >
              {props.options.sms_text}
            </span>
            {showPhone && (
              <Phone
                ref={floatingPhone.refs.setFloating}
                styles={{
                  position: floatingPhone.strategy,
                  top: floatingPhone.y ?? 0,
                  left: floatingPhone.x ?? 0
                }}
                options={props.options.phone}
                onSubmit={submitSmsForm}
                onClose={closeSmsForm}
              />
            )}
          </Fragment>
        )}
      </span>
    </div>
  )
}

Reminder.propTypes = {
  options: optionsPropTypes('reminder'),
  label: PropTypes.string
}

export default Reminder
